import {streamEmbedUrl, streamKey, distinctLiveStreams} from "../lib/streamEmbed";
import { twitchEmbedUrl } from "../lib/twitchEmbed";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, notificationsRead } from "../api";
import { useAuth } from "../AuthContext";
import { Users, Swords, LogOut, Shield, Home as HomeIcon, ScrollText, Crown, Sparkles, Award, ShieldOff, Trophy, Instagram, Twitch, Music2, Bell, Check, X, ShieldCheck, Tv, ExternalLink, Play } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";

const attendancePromptShownInCurrentPageLoad = new Set();

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setMobile(media.matches);
    media.addEventListener("change", update);
    update();
    return () => media.removeEventListener("change", update);
  }, []);
  const isPlusMember = !!(user?.is_plus || user?.is_personal_plus || user?.plan === "plus");

 const nav = [
    { to: "/", label: "الرئيسية", icon: HomeIcon, id: "nav-home" },
    { to: "/matches", label: "المباريات", icon: Swords, id: "nav-matches" },
    { to: "/tournaments", label: "البطولات", icon: Award, id: "nav-tournaments" },
    { to: "/leagues", label: "الدوريات", icon: Award, id: "nav-leagues" },
    { to: "/clans", label: "الكلانات", icon: Shield, id: "nav-clans" },
    { to: "/players", label: "اللاعبون", icon: Users, id: "nav-players" },
    { to: "/news", label: "الأخبار", icon: ScrollText, id: "nav-news" },
    { to: "/champions", label: "الأبطال", icon: Trophy, id: "nav-champions-factory" },
    { to: "/rivalsguard", label: "RivalsGuard", icon: ShieldCheck, id: "nav-rivalsguard" },
    { to: "/rules", label: "القوانين", icon: ScrollText, id: "nav-rules" },
    { to: "/plus", label: "PLUS", icon: Sparkles, id: "nav-plus" },
    { to: "/blacklist", label: "القائمة السوداء", icon: ShieldOff, id: "nav-blacklist" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TimedAttendanceCheckInBanner user={user} />

      <header className="sticky top-0 z-40 border-b b-soft bg-[#0a0a0bcc] backdrop-blur-xl">
        <div className="w-full h-16 px-6 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center group transition-transform duration-200 hover:scale-[1.05] shrink-0" data-testid="logo-link">
            <div className="h-10 w-10 overflow-hidden relative flex items-center justify-center">
              <img
                src="/logo.png"
                alt="Rivals Icon"
                className="h-10 max-w-none object-cover object-left"
                style={{ width: '160%', transform: 'scale(1.2)' }}
              />
            </div>
          </Link>

          <nav className="hidden md:flex flex-1 items-center justify-center gap-6">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.id}
                className={({ isActive }) =>
                  `shrink-0 px-2.5 lg:px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 whitespace-nowrap ${
                    isActive
                      ? "bg-white/5 text-royalGold-500"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <n.icon size={16} />
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2 sm:gap-3 shrink-0">
            {user && (user.role === "admin" || user.role === "owner") && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-royalGold-500 hover:text-royalGold-400 border border-royalGold-500/30 rounded-md px-3 py-1.5 whitespace-nowrap"
              >
                <Crown size={14} /> {user.role === "owner" ? "لوحة المالك" : "لوحة الإدارة"}
              </NavLink>
            )}

            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/me" data-testid="profile-link" className="flex items-center gap-2 group">
                  <div className="relative h-9 w-9 rounded-md bg-surface border b-soft grid place-items-center font-display text-royalGold-500">
                    {user.username?.[0]?.toUpperCase() || "U"}
                    {isPlusMember && (
                      <Sparkles size={10} className="absolute -top-1 -right-1 text-[#d4af37] drop-shadow-[0_0_8px_rgba(212,175,55,0.55)]" />
                    )}
                  </div>
                  <div className="block text-right leading-tight">
                  <div className="text-xs sm:text-sm font-semibold flex items-center gap-1">
                  {user.username}
                      {isPlusMember && <Sparkles size={10} className="text-[#d4af37] drop-shadow-[0_0_8px_rgba(212,175,55,0.55)]" />}
                    </div>
                    <div className="text-[10px] text-emerald-300/90 font-semibold">🪙 {Number(user?.riv_points || 0)} RIV</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40">
                      {user.role === "owner" ? "مالك" : user.role === "admin" ? "منظم" : isPlusMember ? "Plus" : "لاعب"}
                    </div>
                  </div>
                </Link>
                {!mobile && <NotificationHub key={user.id} user={user} onNavigate={navigate} />}
                <button
                  onClick={() => { logout(); navigate("/auth"); }}
                  data-testid="logout-btn"
                  className="p-2 rounded-md hover:bg-white/5 text-white/50 hover:text-white"
                  title="خروج"
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link
                to="/auth"
                data-testid="auth-link"
                className="px-4 py-2 rounded-md bg-gold-500 text-black font-semibold hover:bg-gold-400 transition-colors"
              >
                دخول
              </Link>
            )}
          </div>

         <div className="md:hidden flex items-center gap-1.5 shrink-0 z-10">    
              {user ? (
              <>
              <Link
                  to="/me"
                  data-testid="profile-link-mobile"
                  className="flex items-center shrink-0 p-1 rounded-md hover:bg-white/5 transition-colors"
                >
                  <div className="relative h-8 w-8 rounded-md bg-surface border b-soft grid place-items-center font-display text-xs text-royalGold-500 shrink-0">
                    {user.username?.[0]?.toUpperCase() || "U"}
                    {isPlusMember && (
                      <Sparkles size={8} className="absolute -top-1 -right-1 text-[#d4af37]" />
                    )}
                  </div>
                </Link>
                {mobile && <NotificationHub key={user.id} user={user} onNavigate={navigate} />}
                <button
                  onClick={() => { logout(); navigate("/auth"); }}
                  data-testid="logout-btn-mobile"
                  className="p-2 rounded-md hover:bg-white/5 text-white/50 hover:text-white"
                  title="خروج"
                >
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                data-testid="auth-link-mobile"
                className="px-3 py-1.5 rounded-md bg-gold-500 text-black text-sm font-semibold hover:bg-gold-400 transition-colors"
              >
                دخول
              </Link>
            )}
          </div>
        </div>

        <nav className="md:hidden flex items-center gap-1.5 px-2 pb-2 overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `snap-start px-3 py-1.5 rounded-md text-xs whitespace-nowrap flex items-center gap-1 border ${n.to === "/rules" ? "whitespace-nowrap" : ""} ${
                  isActive ? "bg-white/5 text-royalGold-500 border-royalGold-500/35" : "text-white/60 border-white/10"
                }`
              }
            >
              <n.icon size={14} />
              {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" || user?.role === "owner" ? (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-md text-xs whitespace-nowrap flex items-center gap-1 ${
                  isActive ? "bg-white/5 text-royalGold-500" : "text-royalGold-500/70"
                }`
              }
            >
              <Crown size={14} /> {user.role === "owner" ? "المالك" : "الإدارة"}
            </NavLink>
          ) : null}
        </nav>
      </header>

      <main className="container py-8">{children}</main>

      {/* زر البثوث المعلق مع القائمة المنسدلة الذكية (Popup) */}
      <FloatingLiveMenu />

      <SiteFooter />
    </div>
  );
}

// مكون القائمة المنسدلة لزر البثوث العائم مع مشغل مباشر داخل المنصة
export function FloatingLiveMenu() {
  const [open, setOpen] = useState(false);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStream, setSelectedStream] = useState(null); // للبث النشط حالياً داخل القائمة

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fetchStreams = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/live-streams", { timeout: 15000 });
        const allStreams = Array.isArray(data) ? data : [];
        if (cancelled) return;
        const uniqueStreams = distinctLiveStreams(allStreams);
        setStreams(uniqueStreams);
        setSelectedStream(current => uniqueStreams.find(stream => streamKey(stream) === streamKey(current)) || uniqueStreams[0] || null);
      } catch {
        if (cancelled) return;
        setStreams([]);
        setSelectedStream(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStreams();
    const interval = setInterval(fetchStreams, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [open]);

  // توليد رابط الـ Embed للمشغل المباشر
  const getEmbedUrl = (stream) => {
    const hostname = window.location.hostname;
    if (!stream) return "";
    const platform = (stream.platform || "").toLowerCase();
    
    if (platform.includes("twitch")) {
      return twitchEmbedUrl(stream, hostname);
    } else if (platform.includes("youtube")) {
      const videoId = stream.video_id || stream.url.split("v=")[1]?.split("&")[0];
      return `https://www.youtube.com/embed/${videoId || stream.username}?autoplay=1`;
    } else if (platform.includes("kick")) {
      return streamEmbedUrl(stream, hostname);
    }
    return stream.url;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="fixed bottom-6 right-6 z-50">
        <PopoverTrigger asChild>
          <button
            type="button"
            className="group relative flex items-center justify-center gap-2 bg-destructive text-white px-6 py-3 rounded-2xl font-black shadow-[0_0_20px_rgba(239,68,68,0.4)] hover:shadow-[0_0_25px_rgba(239,68,68,0.7)] transition-all duration-300 hover:scale-105 cursor-pointer"
            style={{
              animation: "smoothPulse 3s infinite ease-in-out"
            }}
          >
            <span className="text-sm font-black tracking-widest">LIVE</span>
          </button>
        </PopoverTrigger>
      </div>

      <PopoverContent align="end" side="top" className="w-[min(92vw,420px)] p-4 bg-[#111113]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-[999] mb-3">
        <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" />
            <span className="font-display font-black text-sm text-white">مركز البثوث السريع</span>
          </div>
          <span className="text-[10px] uppercase font-mono bg-destructive/10 text-destructive border border-destructive/30 px-2 py-0.5 rounded-full font-bold">
            {streams.length} نشط
          </span>
        </div>

        {/* مشغل الفيديو المباشر داخل القائمة إذا كان هناك بث مختار */}
        {selectedStream && (
          <div className="mb-3 rounded-xl overflow-hidden bg-black border border-white/10 aspect-video relative">
            <iframe
              src={getEmbedUrl(selectedStream)}
              title={selectedStream.username}
              className="w-full h-full border-0"
              allowFullScreen
            />
            <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-md px-2 py-0.5 rounded text-[10px] text-white font-bold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />
              {selectedStream.username}
            </div>
          </div>
        )}

        {/* قائمة اللاعبين المتاحة للتبديل بينهم */}
        <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="text-xs text-white/40 py-6 text-center animate-pulse">جاري فحص البثوث النشطة...</div>
          ) : streams.length === 0 ? (
            <div className="text-xs text-white/40 py-6 text-center space-y-1">
              <p>لا توجد بثوث مباشرة حالياً</p>
              <p className="text-[10px] text-white/30">تأكد من إتمام التحضير في اللوبي</p>
            </div>
          ) : (
            streams.map((s) => {
              const isSelected = streamKey(selectedStream) === streamKey(s);
              return (
                <div
                  key={streamKey(s)}
                  onClick={() => setSelectedStream(s)}
                  className={`cursor-pointer flex items-center gap-3 border rounded-xl p-2 transition group ${
                    isSelected ? "bg-gold-500/10 border-gold-500/50 text-white" : "bg-white/5 border-white/5 hover:bg-white/10"
                  }`}
                >
                  <div className="relative aspect-video w-16 bg-black rounded-lg overflow-hidden shrink-0">
                    {s.thumbnail ? (
                      <img src={s.thumbnail} alt={s.username} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-white/30 text-[9px]">{s.platform}</div>
                    )}
                    <span className="absolute bottom-0.5 right-0.5 bg-destructive text-white text-[7px] font-black px-1 rounded">LIVE</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-white group-hover:text-gold-400 transition truncate">{s.username}</span>
                      <span className="text-[10px] font-mono text-gold-500">{s.platform}</span>
                    </div>
                    <p className="text-[11px] text-white/50 line-clamp-1 mt-0.5">{s.title || "مباراة حية"}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="pt-3 mt-3 border-t border-white/10">
          <Link
            to="/streams"
            onClick={() => setOpen(false)}
            className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-gold-500/10 hover:bg-gold-500/20 text-gold-400 text-xs font-bold transition border border-gold-500/20"
          >
            <span>تكبير ومشاهدة في الصفحة الكاملة</span>
            <ExternalLink size={13} />
          </Link>
        </div>
      </PopoverContent>

      <style>{`
        @keyframes smoothPulse {
          0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 15px rgba(239,68,68,0.4); }
          50% { transform: scale(1.03); opacity: 0.9; box-shadow: 0 0 25px rgba(239,68,68,0.7); }
        }
      `}</style>
    </Popover>
  );
}

export function TimedAttendanceCheckInBanner({ user }) {
  const [resolvedClanId, setResolvedClanId] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [progressShrink, setProgressShrink] = useState(false);
  const [checking, setChecking] = useState(false);

  const dismissBanner = () => {
    setVisible(false);
    setProgressShrink(false);
    setTimeout(() => setMounted(false), 260);
  };

  useEffect(() => {
    let cancelled = false;

    const checkShouldShow = async () => {
      setMounted(false);
      setVisible(false);
      setProgressShrink(false);
      setResolvedClanId(null);
      if (!user?.id) return;
      try {
        const { data } = await api.get("/me/attendance-prompt");
        if (cancelled || !data?.show || !data?.clan_id) return;
        const promptKey = `rivals-attendance-prompt:${user.id}:${data.clan_id}`;
        if (attendancePromptShownInCurrentPageLoad.has(promptKey)) return;
        try {
          if (Date.now() - Number(localStorage.getItem(promptKey) || 0) < 24 * 60 * 60 * 1000) return;
        } catch { /* In-memory suppression remains available. */ }
        attendancePromptShownInCurrentPageLoad.add(promptKey);
        try { localStorage.setItem(promptKey, String(Date.now())); } catch {}
        setResolvedClanId(data.clan_id);
        setMounted(true);
        setVisible(true);
        setTimeout(() => { if (!cancelled) setProgressShrink(true); }, 40);
      } catch { /* A failed membership check must not show a stale reminder. */ }
    };

    checkShouldShow();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.clan_id]);

  useEffect(() => {
    if (!mounted) return undefined;
    const timer = setTimeout(() => {
      dismissBanner();
    }, 7000);
    return () => clearTimeout(timer);
  }, [mounted]);

  const handleCheckIn = async () => {
    if (!resolvedClanId || checking) return;
    setChecking(true);
    try {
      await api.post(`/clans/${resolvedClanId}/attendance/checkin`);
      toast.success("✅ تم تسجيل حضورك بنجاح!");
      dismissBanner();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  if (!mounted) return null;

  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[999] w-[min(92vw,560px)] transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0 pointer-events-none"
      }`}
      data-testid="timed-checkin-banner"
    >
      <div className="relative overflow-hidden bg-[#111113]/95 backdrop-blur-md border border-gray-800 rounded-2xl p-4 shadow-[0_8px_30px_rgba(0,0,0,0.45)]">
        <div className="text-center mb-3">
          <h3 className="text-base md:text-lg font-black text-white">🤔 ودك تحضر الحين؟</h3>
        </div>

        <div className="flex items-center justify-center gap-2.5">
          <button
            type="button"
            onClick={handleCheckIn}
            disabled={checking}
            className="px-5 py-2 rounded-xl bg-emerald-500 text-black font-black hover:bg-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.5)] hover:shadow-[0_0_24px_rgba(16,185,129,0.7)] transition-all disabled:opacity-60"
          >
            {checking ? "..." : "ايه"}
          </button>
          <button
            type="button"
            onClick={dismissBanner}
            className="px-5 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-300 font-bold hover:bg-red-500/20 hover:border-red-400/50 transition-all"
          >
            لا ماني فاضي
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10">
          <div
            className="h-full bg-gradient-to-l from-white to-emerald-400"
            style={{
              width: progressShrink ? "0%" : "100%",
              transitionProperty: "width",
              transitionDuration: "7000ms",
              transitionTimingFunction: "linear",
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function NotificationHub({ user, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("general");
  const [showHistory, setShowHistory] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState("");

  const userId = user?.id;
  const requestRef = useRef(null);
  const reload = useCallback(async () => {
    if (!userId || requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 80 }, signal: controller.signal, timeout: 10000 });
      if (!controller.signal.aborted) {
        setItems(Array.isArray(data?.items) ? data.items : []);
        setUnread(Number(data?.unread_count || 0));
      }
    } catch {
      // Preserve the last successful response during a temporary network failure.
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    reload();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") reload();
    }, 45000);
    return () => {
      clearInterval(timer);
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [reload]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const generalItems = useMemo(() => items.filter((n) => n.channel !== "actionable" && (showHistory || !n.read_at)), [items, showHistory]);
  const actionableItems = useMemo(() => items.filter((n) => n.channel === "actionable" && (showHistory || !n.read_at)), [items, showHistory]);

  const markRead = async (ids = [], markAll = false) => {
    try {
      const { data } = await notificationsRead({ ids, mark_all: markAll });
      if (typeof data?.unread_count === "number") setUnread(data.unread_count);
      setItems((prev) =>
        prev.map((n) => (markAll || ids.includes(n.id) ? { ...n, read_at: new Date().toISOString() } : n))
      );
    } catch {
      // ignore
    }
  };

  const openNotification = async (n) => {
    if (!n) return;
    if (n.id && !n.read_at) await markRead([n.id]);
    const route = n?.data?.route;
    if (route && typeof route === "string") {
      setOpen(false);
      onNavigate(route);
    }
  };

  const handleAction = async (n, action) => {
    if (!n?.id) return;
    setActingId(n.id);
    try {
      if (n.type === "clan_invite") {
        const joinReqId = n?.data?.join_request_id;
        if (!joinReqId) throw new Error("missing join request id");
        await api.post(`/invites/${joinReqId}`, { action });
      } else if (n.type === "clan_challenge") {
        const challengeId = n?.data?.challenge_id;
        if (!challengeId) throw new Error("missing challenge id");
        const { data } = await api.post(`/challenges/${challengeId}`, { action });
        if (action === "accept" && data?.match?.id) {
          setOpen(false);
          onNavigate(`/matches/${data.match.id}`);
        }
      }
      await markRead([n.id]);
      await reload();
    } catch {
      // ignore
    } finally {
      setActingId("");
    }
  };

  const renderNotificationItem = (n) => {
    const isDrawWinner = n?.kind === "draw_winner";
    const msg = isDrawWinner ? "🎉 مبروك! فزت بالسحب." : (n?.message || n?.body || n?.title || "");
    const pending = n?.status === "pending";
    const isActionable = n?.channel === "actionable";
    return (
      <div key={n.id} className="rounded-md border b-soft bg-background/40 p-2.5 space-y-2">
        <button
          type="button"
          onClick={() => openNotification(n)}
          className="w-full text-right"
        >
          <div dir={isDrawWinner ? "rtl" : undefined} className="text-xs text-white/90 leading-relaxed">{msg}</div>
          <div className="mt-1 text-[10px] text-white/35">
            {n?.created_at ? new Date(n.created_at).toLocaleString("ar", isDrawWinner ? { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" } : undefined) : ""}
          </div>
        </button>

        {isDrawWinner && n.data?.discord_url && <a href={n.data.discord_url} target="_blank" rel="noreferrer" dir="rtl" className="inline-flex items-center gap-1 rounded-lg border border-gold-500/25 bg-gold-500/10 px-3 py-2 text-xs font-bold text-gold-400 hover:bg-gold-500/20"><span>استلام الجائزة عبر</span><bdi dir="ltr">Discord</bdi></a>}

        {isActionable && pending && (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={actingId === n.id}
              onClick={() => handleAction(n, "accept")}
              className="px-2.5 py-1.5 rounded bg-emerald-500 text-black text-xs font-bold hover:bg-emerald-400 disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-1">
                <Check size={12} /> {n.type === "clan_challenge" ? "قبول التحدي" : "قبول العقد"}
              </span>
            </button>
            <button
              type="button"
              disabled={actingId === n.id}
              onClick={() => handleAction(n, "reject")}
              className="px-2.5 py-1.5 rounded border b-soft text-xs hover:bg-white/5 disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-1">
                <X size={12} /> رفض
              </span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notifications-trigger"
          className="relative p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-white"
          title="الإشعارات"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -top-1 -left-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-[10px] text-white grid place-items-center font-bold">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,360px)] p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-bold">الإشعارات</div>
          <button
            type="button"
            onClick={() => markRead([], true)}
            className="text-[11px] text-gold-500 hover:text-gold-400"
          >
            تعيين الكل كمقروء
          </button>
        </div>

        <button type="button" onClick={() => setShowHistory(value => !value)} className="mb-3 text-xs text-gold-400 underline">
          {showHistory ? "عرض غير المقروء فقط" : "عرض السجل والمقروء"}
        </button>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="general" className="text-xs">إشعارات عامة</TabsTrigger>
            <TabsTrigger value="actionable" className="text-xs">تنبيهات تفاعلية</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-3">
            <div className="max-h-80 overflow-y-auto space-y-2">
              {loading ? (
                <div className="text-xs text-white/45 py-4 text-center">جاري التحميل...</div>
              ) : generalItems.length === 0 ? (
                <div className="text-xs text-white/45 py-4 text-center">لا توجد إشعارات عامة</div>
              ) : (
                generalItems.map(renderNotificationItem)
              )}
            </div>
          </TabsContent>

          <TabsContent value="actionable" className="mt-3">
            <div className="max-h-80 overflow-y-auto space-y-2">
              {loading ? (
                <div className="text-xs text-white/45 py-4 text-center">جاري التحميل...</div>
              ) : actionableItems.length === 0 ? (
                <div className="text-xs text-white/45 py-4 text-center">لا توجد تنبيهات تفاعلية</div>
              ) : (
                actionableItems.map(renderNotificationItem)
              )}
            </div>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}

function SiteFooter() {
  const links = [
    {
      label: "X",
      href: "https://x.com/rivals__es?s=11",
      testid: "footer-x",
      icon: (
        <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="currentColor">
          <path d="M18.901 1.153h3.68l-8.03 9.172L24 22.847h-7.406l-5.8-7.584-6.638 7.584H.474l8.59-9.815L0 1.154h7.594l5.243 6.932zM17.607 20.64h2.039L6.486 3.244H4.298z" />
        </svg>
      ),
    },
    { label: "Twitch", href: "https://www.twitch.tv/rivals_es", testid: "footer-twitch", icon: <Twitch size={16} /> },
    { label: "Instagram", href: "https://www.instagram.com/rivals_es?igsh=ZWoyc3h0am8wdnF1&utm_source=qr", testid: "footer-instagram", icon: <Instagram size={16} /> },
    { label: "TikTok", href: "https://www.tiktok.com/@rivals__es?_r=1&_t=ZS-981M5GVV4Jo", testid: "footer-tiktok", icon: <Music2 size={16} /> },
  ];
  return (
    <footer className="border-t b-soft mt-12 bg-surface/40" data-testid="site-footer">
      <div className="container py-10 grid md:grid-cols-3 gap-8">
        <div>
          <div className="font-display font-black text-xl text-gold-500">RIVALS</div>
          <p className="text-xs text-white/50 mt-2 leading-relaxed">
            دوري Call of Duty العربي. كلانات، بطولات، شات مباشر — كل شيء تحتاجه في مكان واحد.
          </p>
          <a
            href="https://eauthenticate.saudibusiness.gov.sa/certificate-details/0000309390"
            target="_blank"
            rel="noreferrer"
            aria-label="شعار توثيق التجارة الإلكترونية"
            title="توثيق التجارة الإلكترونية"
            style={{
              display: "inline-block",
              marginTop: "15px",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              const img = e.currentTarget.querySelector("img");
              if (img) {
                img.style.opacity = "1";
              }
            }}
            onMouseOut={(e) => {
              const img = e.currentTarget.querySelector("img");
              if (img) {
                img.style.opacity = "0.8";
              }
            }}
          >
            <img
              src="/sbc-dark.png"
              alt="شعار توثيق التجارة الإلكترونية"
              className="w-[230px] sm:w-[280px] lg:w-[340px] h-auto"
              style={{
                opacity: 0.8,
                transition: "all 0.3s ease",
              }}
            />
          </a>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">روابط مفيدة</div>
          <ul className="space-y-1.5 text-sm">
            <li><a href="/rules" className="text-white/70 hover:text-gold-500">القوانين</a></li>
            <li><a href="/leagues" className="text-white/70 hover:text-gold-500">الدوريات</a></li>
            <li><a href="/rivalsguard" className="text-white/70 hover:text-gold-500">نظام RivalsGuard</a></li>
            <li><a href="/plus" className="text-white/70 hover:text-gold-500">PLUS</a></li>
            <li><a href="/blacklist" className="text-white/70 hover:text-gold-500">القائمة السوداء</a></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">تابعنا</div>
          <div className="flex flex-wrap gap-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                data-testid={l.testid}
                aria-label={l.label}
                title={l.label}
                className="h-9 w-9 rounded-md border b-soft hover:border-gold-500/40 text-white/70 hover:text-gold-500 inline-flex items-center justify-center"
              >
                {l.icon}
              </a>
            ))}
          </div>
          <a href="mailto:1rivalsgg@gmail.com" className="block text-[11px] text-white/40 mt-3 hover:text-gold-500">
            1rivalsgg@gmail.com
          </a>
        </div>    
      </div>
      <div className="border-t b-soft py-4 text-center text-[10px] text-white/30 uppercase tracking-widest">
        © {new Date().getFullYear()} RIVALS جميع الحقوق محفوظة
      </div>
    </footer>
  );
}
